import { ResearchQuestion } from '../../types/fieldResearch';

interface Props {
  questions: ResearchQuestion[];
  answers: Record<string, string | string[] | number>;
  onChange: (id: string, value: string | string[] | number) => void;
}

const inputCls =
  'w-full bg-[#1a1640] border border-white/15 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-purple-500/60 focus:border-purple-500/60 transition';

export default function DynamicQuestionRenderer({ questions, answers, onChange }: Props) {
  const toggleCheckbox = (id: string, option: string) => {
    const current = (answers[id] as string[]) || [];
    const updated = current.includes(option)
      ? current.filter((v) => v !== option)
      : [...current, option];
    onChange(id, updated);
  };

  return (
    <div className="space-y-5">
      <h2 className="text-sm font-bold text-white">Perguntas da Pesquisa</h2>

      {questions.map((q) => (
        <div key={q.id} className="space-y-2">
          <label className="block text-sm font-medium text-white/80">
            {q.label}
            {q.required && <span className="text-red-400 ml-1">*</span>}
          </label>

          {/* Radio */}
          {q.type === 'radio' &&
            q.options?.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(q.id, opt)}
                className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all active:scale-95 ${
                  answers[q.id] === opt
                    ? 'bg-purple-600/25 border-purple-500/60 text-white'
                    : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                }`}
              >
                <span
                  className={`inline-block w-3.5 h-3.5 rounded-full border mr-2.5 align-middle transition-colors ${
                    answers[q.id] === opt
                      ? 'bg-purple-500 border-purple-400'
                      : 'border-white/30'
                  }`}
                />
                {opt}
              </button>
            ))}

          {/* Checkbox */}
          {q.type === 'checkbox' &&
            q.options?.map((opt) => {
              const checked = ((answers[q.id] as string[]) || []).includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => toggleCheckbox(q.id, opt)}
                  className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all active:scale-95 ${
                    checked
                      ? 'bg-pink-600/25 border-pink-500/60 text-white'
                      : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                  }`}
                >
                  <span
                    className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded border mr-2.5 align-middle transition-colors ${
                      checked ? 'bg-pink-500 border-pink-400' : 'border-white/30'
                    }`}
                  >
                    {checked && (
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                        <path d="M1 4L3 6L7 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    )}
                  </span>
                  {opt}
                </button>
              );
            })}

          {/* Text */}
          {q.type === 'text' && (
            <input
              className={inputCls}
              value={(answers[q.id] as string) || ''}
              onChange={(e) => onChange(q.id, e.target.value)}
              placeholder={q.label}
            />
          )}

          {/* Textarea */}
          {q.type === 'textarea' && (
            <textarea
              className={`${inputCls} resize-none h-20`}
              value={(answers[q.id] as string) || ''}
              onChange={(e) => onChange(q.id, e.target.value)}
              placeholder={q.label}
            />
          )}

          {/* Number */}
          {q.type === 'number' && (
            <input
              type="number"
              className={inputCls}
              value={(answers[q.id] as number) !== undefined ? String(answers[q.id]) : ''}
              onChange={(e) =>
                onChange(q.id, e.target.value === '' ? 0 : parseFloat(e.target.value))
              }
              placeholder={q.label}
            />
          )}
        </div>
      ))}
    </div>
  );
}
